import React from "react";
import PropTypes from "prop-types";
import { Alert, Button, Card, Col, Empty, Row, Skeleton, Tag, Typography } from "antd";

import useCommercialCatalog from "../../hooks/useCommercialCatalog";

const { Text } = Typography;

const PLATFORM_LABELS = {
    PP: "Production agricole",
    PEC: "E-commerce agricole",
    AGRI_HERO: "IA & mécanisation",
    ACCESS: "Usages"
};

const CYCLE_LABELS = { MONTHLY: "mois", ANNUAL: "an" };

/**
 * Montant dans la devise de facturation du pays, avec le symbole et les décimales renvoyés par
 * le backend (« 6 560 FCFA », « 16 399,00 ₦ ») : aucune table de devises côté frontend.
 */
const formatAmount = (amount, currency) =>
    `${new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: currency.minorUnits,
        maximumFractionDigits: currency.minorUnits
    }).format(Number(amount))} ${currency.symbol}`;

/**
 * Prix d'un cycle, dans la devise de facturation du pays uniquement : jamais de repli sur le
 * prix de référence EUR. Sans taux de change, la devise reste affichée sans montant.
 */
const CyclePrice = ({ price, currency, accent, main }) => {
    const unit = CYCLE_LABELS[price.billingCycle] || price.billingCycle;
    const Line = main ? "h2" : "p";
    const style = main ? { color: accent, margin: "10px 0 0" } : { margin: "2px 0 0", fontSize: 14, fontWeight: 500 };
    if (price.availability === "AVAILABLE") {
        return <Line style={style}>{formatAmount(price.price.amount, currency)} / {unit}</Line>;
    }
    if (price.availability === "PRICE_UNAVAILABLE") {
        return <Line style={style}>{currency.symbol} — / {unit}</Line>;
    }
    return <Line style={style}><Text type="secondary">Tarif en cours de définition</Text></Line>;
};

CyclePrice.propTypes = {
    price: PropTypes.object.isRequired,
    currency: PropTypes.object.isRequired,
    accent: PropTypes.string.isRequired,
    main: PropTypes.bool
};

const Prices = ({ prices, currency, accent }) => {
    const ordered = ["MONTHLY", "ANNUAL"].map(cycle => prices.find(p => p.billingCycle === cycle)).filter(Boolean);
    if (!ordered.length) {
        return <Text type="secondary">Tarif en cours de définition</Text>;
    }
    const unavailable = ordered.some(p => p.availability === "PRICE_UNAVAILABLE");
    return (
        <div>
            {ordered.map((price, index) => (
                <CyclePrice key={price.billingCycle} price={price} currency={currency} accent={accent} main={index === 0} />
            ))}
            {unavailable && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Tarif local bientôt disponible · Devise : {currency.code} ({currency.symbol})
                </Text>
            )}
        </div>
    );
};

Prices.propTypes = {
    prices: PropTypes.array.isRequired,
    currency: PropTypes.object.isRequired,
    accent: PropTypes.string.isRequired
};

/** Capabilities propres à l'offre, regroupées par plateforme ; les offres incluses sont citées. */
const Capabilities = ({ offer }) => {
    const own = offer.capabilities.filter((capability) => capability.grantedBy === offer.code);
    const byPlatform = own.reduce((groups, capability) => {
        (groups[capability.platform] = groups[capability.platform] || []).push(capability);
        return groups;
    }, {});
    return (
        <div>
            {offer.includedOffers.length > 0 && (
                <p style={{ marginBottom: 8 }}>
                    <Text strong>Inclut : </Text>
                    {offer.includedOffers.map((code) => <Tag key={code}>{code}</Tag>)}
                </p>
            )}
            {Object.entries(byPlatform).map(([platform, capabilities]) => (
                <div key={platform} style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{PLATFORM_LABELS[platform] || platform}</Text>
                    <ul style={{ paddingLeft: 16, margin: "4px 0" }}>
                        {capabilities.map((capability) => (
                            <li key={capability.code}>
                                {capability.description}
                                {capability.pricingMode === "CONFIGURATION_REQUIRED" && (
                                    <Text type="secondary"> (tarif à définir)</Text>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
};

Capabilities.propTypes = {
    offer: PropTypes.object.isRequired
};

/**
 * Offres d'un profil (PRODUCTEUR, SPECULATEUR, EXPORTATEUR) servies par
 * assiganto-commercial-financial-platform : aucun prix ni aucune composition d'offre
 * n'est défini dans le frontend.
 */
const CommercialCatalogPlans = ({ profile, accent = "#52c41a", onSubscribe }) => {
    const { loading, error, catalog } = useCommercialCatalog(profile);

    if (loading) {
        return <Skeleton active paragraph={{ rows: 6 }} />;
    }
    if (error) {
        return <Alert type="error" showIcon title="Le catalogue des offres est momentanément indisponible." />;
    }
    if (!catalog?.offers?.length) {
        return <Empty description="Aucune offre pour ce profil." />;
    }

    return (
        <Row gutter={[24, 24]}>
            {catalog.offers.map((offer) => {
                const localPriceMissing = offer.prices.some((p) => p.availability === "PRICE_UNAVAILABLE");
                return (
                    <Col xs={24} sm={12} lg={8} key={offer.code}>
                        <Card
                            title={offer.name}
                            hoverable
                            style={{ height: "100%" }}
                            styles={{ header: { fontSize: 18, fontWeight: 600 } }}
                        >
                            {offer.status === "CONFIGURATION_REQUIRED" && (
                                <Tag color="default" style={{ marginBottom: 8 }}>Offre en préparation</Tag>
                            )}
                            <Prices prices={offer.prices} currency={catalog.currency} accent={accent} />
                            <p style={{ marginTop: 12 }}>{offer.description}</p>
                            <Capabilities offer={offer} />
                            <Button
                                className="application-button"
                                block
                                style={{ marginTop: 16 }}
                                disabled={!offer.subscribable}
                                onClick={() => onSubscribe(offer)}
                            >
                                {offer.subscribable ? "Souscrire" : localPriceMissing ? "Tarif local indisponible" : "Tarif en cours de définition"}
                            </Button>
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );
};

CommercialCatalogPlans.propTypes = {
    profile: PropTypes.oneOf(["PRODUCTEUR", "SPECULATEUR", "EXPORTATEUR"]).isRequired,
    accent: PropTypes.string,
    onSubscribe: PropTypes.func.isRequired
};

export default CommercialCatalogPlans;
